// Scheduled sends (owner, 2026-10-10): the clock next to Send in Conversations.
// Every time is Morocco's wall clock (Africa/Casablanca) — the owner's day and
// the clients' — whatever time zone the browser happens to be in.
//
// Guard: src/lib/scheduleTime.test.ts

export const SCHEDULE_TZ = 'Africa/Casablanca';

/** Minutes the zone is ahead of UTC at this instant. */
function offsetMinutes(ms: number, tz = SCHEDULE_TZ): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(ms));
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour') % 24, n('minute'), n('second'));
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60_000);
}

/** The wall-clock date and time in Morocco at this instant. */
export function casaParts(ms: number): { y: number; m: number; d: number; hh: number; mm: number } {
  const t = new Date(ms + offsetMinutes(ms) * 60_000);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate(), hh: t.getUTCHours(), mm: t.getUTCMinutes() };
}

/** The instant a Morocco wall-clock time names. */
export function casaToMs(y: number, m: number, d: number, hh: number, mm: number): number {
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const first = guess - offsetMinutes(guess) * 60_000;
  return guess - offsetMinutes(first) * 60_000;
}

/** « 2026-10-11 » / « 09:00 » from an input pair, or null. */
export function casaInputToMs(date: string, time: string): number | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const tm = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dm || !tm) return null;
  return casaToMs(+dm[1], +dm[2], +dm[3], +tm[1], +tm[2]);
}

export const pad2 = (n: number) => String(n).padStart(2, '0');
export const casaDateInput = (ms: number) => { const p = casaParts(ms); return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`; };

export interface QuickSlot { key: 'this_morning' | 'tonight' | 'tomorrow_morning' | 'tomorrow_evening'; label: string; ms: number }

/** This morning 09:00 (after midnight), tonight 21:00 (while still ahead), tomorrow 09:00, tomorrow 21:00. */
export function quickSlots(nowMs: number): QuickSlot[] {
  const p = casaParts(nowMs);
  const tomorrow = casaParts(casaToMs(p.y, p.m, p.d, 12, 0) + 86_400_000);
  const out: QuickSlot[] = [];
  const thisMorning = casaToMs(p.y, p.m, p.d, 9, 0);
  if (p.hh < 8 && thisMorning - nowMs >= 15 * 60_000) out.push({ key: 'this_morning', label: 'Ce matin', ms: thisMorning });
  const tonight = casaToMs(p.y, p.m, p.d, 21, 0);
  if (tonight - nowMs >= 15 * 60_000) out.push({ key: 'tonight', label: 'Ce soir', ms: tonight });
  out.push({ key: 'tomorrow_morning', label: 'Demain matin', ms: casaToMs(tomorrow.y, tomorrow.m, tomorrow.d, 9, 0) });
  out.push({ key: 'tomorrow_evening', label: 'Demain soir', ms: casaToMs(tomorrow.y, tomorrow.m, tomorrow.d, 21, 0) });
  return out;
}

/** « Aujourd'hui 21:00 », « Demain 09:00 », « sam. 18 oct. 09:00 » — in Morocco time.
 *  `words` carries the page's language (the admin also runs in English). */
export function whenLabel(ms: number, nowMs: number, words: { today?: string; tomorrow?: string; locale?: string } = {}): string {
  const p = casaParts(ms);
  const today = casaParts(nowMs);
  const tomorrow = casaParts(casaToMs(today.y, today.m, today.d, 12, 0) + 86_400_000);
  const hm = `${pad2(p.hh)}:${pad2(p.mm)}`;
  const same = (a: { y: number; m: number; d: number }) => a.y === p.y && a.m === p.m && a.d === p.d;
  if (same(today)) return `${words.today ?? 'Aujourd’hui'} ${hm}`;
  if (same(tomorrow)) return `${words.tomorrow ?? 'Demain'} ${hm}`;
  const day = new Intl.DateTimeFormat(words.locale ?? 'fr-FR', { timeZone: SCHEDULE_TZ, weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(ms));
  return `${day} ${hm}`;
}
